import { cn } from '@sohanemon/utils';
import { Iconify, MountAfter } from '@sohanemon/utils/components';
import { useScheduledEffect } from '@sohanemon/utils/hooks';
import { m } from '@sohanscript/i18n';
import { usePerformant } from '@sohanscript/lib/hooks/performant';
import { Motion } from '@sohanscript/ui/motion';
import { SrOnly } from '@sohanscript/ui/sr-only';
import { useRouter } from '@tanstack/react-router';
import { useLenis } from 'lenis/react';
import type * as React from 'react';
import { Button } from '#/components/ui/button.tsx';
import { WindowRenderer } from '#/components/ui/window-renderer.tsx';
import { WINDOW_CONFIGS } from '#/lib/config/windows.tsx';
import { useIsFocused, useWindowStore } from '#/lib/store/window-manager.ts';

type DetatchedWindowProps = React.ComponentProps<'div'>;

export function DetatchedWindow({ className, ...props }: DetatchedWindowProps) {
	const { openWindow, closeAllWindow } = useWindowStore();
	const lenis = useLenis();
	const performant = usePerformant();
	useScheduledEffect(() => {
		closeAllWindow();
		openWindow('about');
		setTimeout(() => openWindow('hero'), 500);
	});

	return (
		<div
			className={cn(
				'translate-0 relative h-(--height-except-navbar)',
				className,
			)}
			{...props}
		>
			<WindowRenderer maximizedMode="cover" />
			<MountAfter delay={1500}>
				<Motion
					initial="bottom-xs"
					className="absolute inset-x-10 bottom-10 -z-1 flex justify-center gap-3"
				>
					{WINDOW_CONFIGS.filter((cfg) =>
						['hero', 'about'].includes(cfg.id),
					).map((cfg) => {
						return <DetatchedWindowItems key={cfg.id} id={cfg.id} />;
					})}
					{performant && (
						<Button
							variant="outline"
							tooltip={m.detatched_scroll_down()}
							size="icon"
							className="animate-pulse"
							onClick={() => {
								lenis?.scrollTo(window.innerHeight, {
									duration: 3,
									lerp: 0.05,
								});
							}}
						>
							<Iconify icon="hugeicons:mouse-scroll-02" className="size-6" />
							<SrOnly>{m.detatched_scroll_down()}</SrOnly>
						</Button>
					)}
				</Motion>
			</MountAfter>
		</div>
	);
}

function DetatchedWindowItems({ id }: { id: string }) {
	const { windows, openWindow } = useWindowStore();
	const win = windows.find((w) => w.id === id);
	const isMinimized = win?.status === 'minimized';
	const isActive = useIsFocused(id);
	const lenis = useLenis();

	const router = useRouter();

	function handleDockClick(id: string) {
		lenis?.scrollTo(0);
		const cfg = WINDOW_CONFIGS.find((c) => c.id === id);
		if (cfg && typeof cfg.element === 'string') {
			router.navigate({ to: cfg.element });
			return;
		}
		openWindow(id);
	}

	return (
		<div className="relative">
			<Button
				variant={isActive ? 'default' : 'outline'}
				onClick={() => handleDockClick(id)}
			>
				<Iconify icon={win?.icon ?? ''} className="size-6" />
				{win?.title}
			</Button>
			{isMinimized && (
				<div
					className={cn(
						'absolute inset-x-0 -bottom-2 mx-auto h-1 w-1/8 rounded-full bg-primary',
						isMinimized && 'bg-primary/50',
					)}
				/>
			)}
		</div>
	);
}
